import { useEffect, useRef, useState } from 'react';
import { useIntersectionObserver } from '@jk-core/hooks';
import { cn } from '@jk-core/utils';
import styles from './Calendar.module.scss';
import DateLabel from './components/DateLabel';
import DayTile from './components/DayTile';
import { CalendarRange } from './type';
import getWeeksInMonth from './utils/getWeeksInMonth';

interface CalendarProps {
  className?: string;
  date: CalendarRange;
  onChange:(date:CalendarRange)=>void;
  min: Date;
  max: Date;
}

export default function ScrollCalendar({
  className = '', date: selectedDate = [null, null], onChange,
  min, max,
}: CalendarProps) {
  const maxCount = new Date().getMonth() - min.getMonth()
    + 12 * (new Date().getFullYear() - min.getFullYear());
  const scrollRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);
  const [viewDate, setViewDate] = useState<Date>(selectedDate?.[0] || new Date());
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const [calendarCount, setCalendarCount] = useState(Array.from({
    length: Math.abs(viewDate.getMonth() - new Date().getMonth()
      + (12 * (viewDate.getFullYear() - new Date().getFullYear() - 1))),
  }).map((_, idx) => idx));

  const onDayClick = (day: Date, maintenance = false) => {
    if (!maintenance) setViewDate(day);
    if (!selectedDate[0] || (!!selectedDate[0] && !!selectedDate[1])) {
      onChange([day, null]);
    }
    if (!!selectedDate[0] && !selectedDate[1]) {
      if (day < selectedDate[0]) {
        onChange([day, null]);
      } else {
        onChange([selectedDate[0], day]);
      }
    }
  };

  const handleDateChange = (count: number) => {
    const newDate = new Date();
    newDate.setMonth(newDate.getMonth() - count);
    return newDate;
  };

  useEffect(() => {
    if (viewDate > max) {
      setViewDate(max);
    }
    if (viewDate < min) {
      setViewDate(min);
    }
  }, [viewDate, max, min]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, []);

  useIntersectionObserver({
    target: targetRef as React.RefObject<HTMLDivElement>,
    parent: scrollRef as React.RefObject<HTMLDivElement>,
    callback: () => {
      if (maxCount < calendarCount.length) return;
      const length = maxCount - (calendarCount.length - 1) > 12
        ? 12 : maxCount - (calendarCount.length - 1);
      setCalendarCount((prev) => [...prev,
        ...Array.from({ length }).map((_, i) => prev.length + i)]);
    },
    options: { threshold: 0.1 },
  });

  return (
    <div className={cn({
      [styles.calendar]: true,
      [styles['calendar--range']]: true,
      [styles['calendar--scroll']]: true,
      [className]: !!className,
    })}
    >
      {/* 일/월/년 선택 버튼 */}
      <div className={styles.calendar__scroll} ref={scrollRef}>
        {calendarCount.map((count) => (
          <div
            className={styles['calendar__scroll--wrapper']}
            key={count}
            ref={(maxCount > (calendarCount.length - 1)
              && count === calendarCount.length - 2) ? targetRef : null}
          >
            <DateLabel
              viewDate={handleDateChange(count)}
              method="day"
              onArrowClick={() => { }}
              disabled={() => false}
              hideArrow="all"
              range
            />
            <DayTile
              style={{ height: 'auto', minHeight: 'auto' }}
              hoverDate={hoverDate}
              setHoverDate={(day) => setHoverDate(day)}
              selectedDate={selectedDate}
              weeksInMonth={getWeeksInMonth(handleDateChange(count))}
              handleDayClick={onDayClick}
              hideAfter
              hideBefore
              max={max}
              min={min}
              range
              scroll
            />
          </div>
        ))}

      </div>
    </div>
  );
}
